/**
 * Chat Panel Component for Admin Frontend
 * Real-time messaging with Socket.io, file upload, typing indicators, and embedded payment requests.
 * Adapted from DOUBTQ CHATBOT/admin/src/components/ChatPanel.jsx
 */

import React, { useState, useEffect, useRef } from 'react';
import { useChatAuth } from '../../hooks/ChatAuthContext';
import { useChatNotification } from '../../hooks/ChatNotificationContext';
import { getChatSocket } from '../../services/chatSocket';
import chatApi, { CHAT_BASE_URL } from '../../services/chatApi';
import PaymentRequestForm from './PaymentRequestForm';
import './ChatStyles.css';

const ChatPanel = ({ orderId, onClose }) => {
    const { user } = useChatAuth();
    const { setActiveOrderId, markOrderRead } = useChatNotification();
    const socket = getChatSocket();

    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [userTyping, setUserTyping] = useState(false);
    const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'payment'

    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    // Register active order to suppress toast notifications + clear badge
    useEffect(() => {
        setActiveOrderId(orderId);
        markOrderRead(orderId);       // ← clear the unread badge immediately
        return () => setActiveOrderId(null);
    }, [orderId, setActiveOrderId, markOrderRead]);

    // Scroll chat to bottom
    const scrollToBottom = () => {
        requestAnimationFrame(() => {
            setTimeout(() => {
                if (messagesContainerRef.current) {
                    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                }
            }, 0);
        });
    };

    // Auto-scroll when messages update
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Initial fetch of messages
    useEffect(() => {
        const fetchMessages = async () => {
            try {
                const response = await chatApi.get(`/messages/${orderId}`);
                setMessages(response.data.messages || []);

                // Auto mark as read when admin opens chat
                await chatApi.post(`/messages/${orderId}/read`, { userRole: 'admin' });

                if (socket) {
                    socket.emit('messages-read', { orderId, userRole: 'admin' });
                }

            } catch (error) {
                console.error('Error fetching admin messages:', error);
            } finally {
                setLoading(false);
                // Scroll AFTER loading is done and React renders the messages
                scrollToBottom();
            }
        };

        if (orderId) {
            fetchMessages();
        }
    }, [orderId, socket]);

    // Socket listeners setup
    useEffect(() => {
        if (!socket || !orderId) return;

        socket.emit('join-order', { orderId });

        const handleNewMessage = (message) => {
            setMessages((prev) => [...prev, message]);

            // If we're focused on this chat, mark incoming messages as read
            socket.emit('messages-read', { orderId, userRole: 'admin' });
        };

        const handleUserTyping = ({ userRole, isTyping }) => {
            if (userRole === 'student') { // Changed from 'user' to 'student'
                setUserTyping(isTyping);
            }
        };

        const handlePaymentRequest = (paymentMessage) => {
            setMessages((prev) => [...prev, paymentMessage]);
            setActiveTab('chat');
        };

        const handlePaymentConfirmed = () => {
            chatApi.get(`/messages/${orderId}`).then((res) => {
                setMessages(res.data.messages);
            });
        };

        socket.on('new-message', handleNewMessage);
        socket.on('user-typing', handleUserTyping);
        socket.on('payment-request', handlePaymentRequest);
        socket.on('payment-confirmed', handlePaymentConfirmed);

        return () => {
            socket.emit('leave-order', { orderId });
            socket.off('new-message', handleNewMessage);
            socket.off('user-typing', handleUserTyping);
            socket.off('payment-request', handlePaymentRequest);
            socket.off('payment-confirmed', handlePaymentConfirmed);
        };
    }, [socket, orderId]);

    // Emit typing indicator
    const handleTyping = () => {
        if (!socket) return;

        socket.emit('typing-start', { orderId, userRole: 'admin' });

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        typingTimeoutRef.current = setTimeout(() => {
            socket.emit('typing-stop', { orderId, userRole: 'admin' });
        }, 2500);
    };

    // Send text message
    const handleSendMessage = async () => {
        if (!newMessage.trim() || sending) return;

        setSending(true);

        socket.emit('send-message', {
            orderId,
            senderId: user?.id || 'admin',
            senderRole: 'admin',
            messageType: 'TEXT',
            content: newMessage.trim(),
        });

        setNewMessage('');
        setSending(false);

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        socket.emit('typing-stop', { orderId, userRole: 'admin' });
    };

    // File upload handler
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 15 * 1024 * 1024) {
            alert('File size must be less than 15MB');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const uploadResponse = await chatApi.post('/files/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const fileData = uploadResponse.data.file;

            socket.emit('send-message', {
                orderId,
                senderId: user?.id || 'admin',
                senderRole: 'admin',
                messageType: 'FILE',
                fileUrl: fileData.url,
                fileName: fileData.originalName,
                fileSize: fileData.size,
            });
        } catch (error) {
            console.error('Admin File upload error:', error);
            alert('Failed to upload file');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    // Secure file download helper for admin
    const handleFileDownload = async (fileUrl, fileName) => {
        try {
            const token = localStorage.getItem('authToken');

            // fileUrl already includes '/api/' so avoid double slashing
            const downloadUrl = fileUrl.startsWith('/') ? `${CHAT_BASE_URL}${fileUrl}` : `${CHAT_BASE_URL}/${fileUrl}`;

            const response = await fetch(downloadUrl, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
            alert('Failed to download file.');
        }
    };

    // ── Currency Helper ───────────────────────────────────────────────────────
    const getCurrencySymbol = (currency) => {
        return `${currency} `;
    };

    // ── Render ────────────────────────────────────────────────────────────────

    // Render individual message bubble
    const renderMessage = (msg) => {
        // Admin perspective: sent by 'admin' means 'You'
        const isAdmin = msg.senderRole === 'admin';
        // NOTE: ChatStyles.css has admin-specific overrides that swap alignment:
        //   .from-admin → flex-end (RIGHT)  |  .from-user → flex-start (LEFT)
        const rowClass = `chat-message-row ${isAdmin ? 'from-admin' : 'from-user'}`;

        const timestamp = (() => {
            const d = new Date(msg.createdAt || msg.timestamp);
            return !isNaN(d.getTime())
                ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : '';
        })();

        return (
            <div key={msg.id || msg._id} className={rowClass}>
                <div className="chat-message-wrapper">
                    <p className="chat-sender-name">
                        {isAdmin ? 'You (Admin)' : 'Student'}
                    </p>

                    {msg.messageType === 'TEXT' && (
                        <div className={isAdmin ? 'chat-bubble-user' : 'chat-bubble-admin'}>
                            <p>{msg.content}</p>
                        </div>
                    )}

                    {msg.messageType === 'FILE' && (
                        <div className={isAdmin ? 'chat-bubble-user' : 'chat-bubble-admin'}>
                            <div className="chat-file-info">
                                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                                <div>
                                    <p>{msg.fileName}</p>
                                    <button
                                        onClick={() => handleFileDownload(msg.fileUrl, msg.fileName)}
                                        className="chat-file-download"
                                    >
                                        Download
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {msg.messageType === 'PAYMENT' && (
                        <div className="chat-payment-card">
                            <p className="chat-payment-title">💳 Requested Payment</p>
                            <p className="chat-payment-amount">
                                {getCurrencySymbol(msg.paymentData?.currency || 'USD')}{msg.paymentData?.amount || msg.paymentAmount}
                            </p>
                            {msg.paymentData?.status === 'pending' ? (
                                <div style={{ background: '#fef3c7', color: '#92400e', padding: '8px 12px', borderRadius: '8px', fontSize: '0.875rem', fontWeight: 500, textAlign: 'center' }}>
                                    ⏳ Pending Student Payment
                                </div>
                            ) : (
                                <div className="chat-payment-completed">
                                    ✅ Paid Confirmed
                                </div>
                            )}
                        </div>
                    )}

                    <p className="chat-message-timestamp">
                        {timestamp}
                    </p>
                </div>
            </div>
        );
    };

    return (
        <div className="chat-panel-overlay">
            <div className="chat-module-container" style={{ display: 'flex', flexDirection: 'column' }}>

                {/* Header */}
                <div className="chat-panel-header">
                    <div>
                        <h3>Chat: {orderId}</h3>
                        <div className="chat-panel-badges">
                            <span className="chat-badge-status chat-badge-paid">Admin</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="chat-panel-close">
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Tabs */}
                <div className="chat-tabs">
                    <button
                        className={`chat-tab ${activeTab === 'chat' ? 'active' : ''}`}
                        onClick={() => setActiveTab('chat')}
                    >
                        Messages
                    </button>
                    <button
                        className={`chat-tab ${activeTab === 'payment' ? 'active' : ''}`}
                        onClick={() => setActiveTab('payment')}
                    >
                        Request Payment
                    </button>
                </div>

                {/* Dynamic Content Body */}
                <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    {activeTab === 'chat' && (
                        <>
                            <div className="chat-messages-area" ref={messagesContainerRef}>
                                {loading ? (
                                    <div className="chat-loading">
                                        <div className="chat-spinner"></div>
                                    </div>
                                ) : messages.length === 0 ? (
                                    <div className="chat-empty">
                                        No messages for order {orderId} yet.
                                    </div>
                                ) : (
                                    messages.map(renderMessage)
                                )}

                                {userTyping && (
                                    <div className="chat-typing-indicator" style={{ justifyContent: 'flex-start' }}>
                                        <div className="chat-typing-dots">
                                            <div className="chat-typing-dot"></div>
                                            <div className="chat-typing-dot"></div>
                                            <div className="chat-typing-dot"></div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {uploading && (
                                <div className="chat-upload-progress">
                                    <p>Uploading file wrapper...</p>
                                </div>
                            )}

                            {/* Input Area */}
                            <div className="chat-input-area">
                                <div className="chat-input-row">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        onChange={handleFileUpload}
                                        className="chat-file-input"
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.pptx,.zip"
                                    />
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        className="chat-attach-btn"
                                        title="Attach file"
                                    >
                                        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                        </svg>
                                    </button>

                                    <input
                                        type="text"
                                        value={newMessage}
                                        onChange={(e) => {
                                            setNewMessage(e.target.value);
                                            handleTyping();
                                        }}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                                        placeholder="Message student..."
                                        className="chat-text-input"
                                    />

                                    <button
                                        onClick={handleSendMessage}
                                        disabled={!newMessage.trim() || sending}
                                        className="chat-send-btn"
                                    >
                                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'payment' && (
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            <PaymentRequestForm
                                orderId={orderId}
                                onSuccess={() => setActiveTab('chat')}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatPanel;
